import React, { useEffect } from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';

import { Modal } from 'js/design_system/Modal';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { TextButton } from 'js/design_system/TextButton';

import $image_url from 'js/utils/image_url';
import ImpressionWatcher from 'js/utils/impressions/ImpressionWatcher';
import {
  recordClickthrough,
  recordDismissal,
  recordShow,
} from 'js/utils/dismissibles';

import SocialProofUniversityFooter from 'js/premium_upgrade/SocialProofUniversityFooter';

const DISMISSIBLE_NAME = 'FullScreenPremiumModal';

// Override some DS modal styles to satisfy the requirements of the full screen modal
const FullScreenModal = styled(Modal)`
  padding: 0;
  height: 100vh;
  flex-direction: column;
  justify-content: flex-start;

  > :global(.ds2-5-modal__container) {
    padding: 0;
    display: flex;
    flex: 1;
    flex-direction: row;
    align-items: stretch;
    max-width: none !important;
    max-height: none !important;
    width: 100% !important;
    height: 100% !important;
    font-family: 'DM Sans', sans-serif;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: white;

  &:hover {
    opacity: 0.7;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex: 1;
  min-width: clamp(510px, 40vw, 576px);
  flex-direction: column;
  align-items: center;
  background: var(--background-beige-light);

  @media (max-width: $ds-desktop-lg) {
    display: none;
  }
`;

const SplashImage = styled.img`
  max-width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
`;

const MainContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: clamp(10px, 5vh, 48px) 24px;
  text-align: center;
  background: var(--background-black, #141413);
`;

const HeadingIcon = styled.img`
  width: 96px;
  height: 96px;

  @media (max-width: $ds-desktop-lg) {
    width: clamp(48px, 10vh, 96px);
    height: clamp(48px, 10vh, 96px);
  }
`;

const HeadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 539px;
  gap: var(--spacing-sm-32, 32px);
  color: var(--text-white, #fff);
  text-align: center;
  font-size: 60px;
  font-style: normal;
  font-weight: 400;
  line-height: 95%;

  @media (max-width: $ds-tablet-sm) {
    font-size: 32px;
    line-height: 38px;
    width: 313px;
  }

  @media (max-width: $ds-desktop-lg) and (max-height: 800px) {
    font-size: 36px;
  }
`;

const TopSpacer = styled.div`
  flex-grow: 3;
`;

const BottomSpacer = styled.div`
  flex-grow: 7;
`;

const FeatureList = styled.div`
  margin-top: 10px;
  display: flex;
  width: 382px;
  flex-direction: column;
  gap: 4px;

  @media (max-width: $ds-tablet-sm) {
    width: 313px;
  }
`;

const CheckIcon = styled(Icon)`
  color: var(--premium-gold);

  @media (max-width: $ds-tablet-sm) {
    font-size: 24px !important;
  }
`;

const FeatureItem = styled.div`
  color: var(--text-white);
  display: flex;
  align-items: center;
  padding: 2px 0;
  gap: var(--spacing-xs-8, 8px);
  font-size: 20px;
  line-height: 26px;

  @media (max-width: $ds-tablet-sm) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const BottomContent = styled.div`
  display: flex;
  width: 398px;
  padding-top: var(--spacing-xs-16, 16px);
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
  margin-top: auto;

  @media (max-width: $ds-tablet-sm) {
    width: 246px;
  }
`;

const DisclaimerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

const Disclaimer = styled.span`
  color: var(--brand-colors-neutral-500, #c8c8cf);
  text-align: center;
  font-size: var(--type-body-xs-size, 12px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-xs-line-height, 16px);
`;

const PremiumButton = styled(Button)`
  font-size: var(--type-body-md-size, 16px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--type-body-md-line-height, 20px);
  font-family: 'DM Sans', sans-serif;
`;

interface Props {
  onClose: () => void;
  trialPrice: string | null | undefined;
  churned: boolean;
}

const FullScreenPremiumModal: React.FC<Props> = ({
  onClose,
  trialPrice,
  churned,
}) => {
  useEffect(() => {
    recordShow(DISMISSIBLE_NAME);
  }, []);

  const onCloseWithRecordDismissal = () => {
    recordDismissal(DISMISSIBLE_NAME);
    onClose();
  };

  const trialAvailable = !!trialPrice;
  let headerText = 'Get Academia Premium';
  let ctaText = 'Become Premium';
  if (trialAvailable) {
    headerText = 'Get 1 month of Academia Premium';
    ctaText = churned
      ? `Reactivate Premium for ${trialPrice}`
      : `Try Premium for ${trialPrice}`;
  }

  return (
    <FullScreenModal
      open={true}
      onClose={onCloseWithRecordDismissal}
      showHeader={false}
    >
      <CloseButton onClick={onCloseWithRecordDismissal} aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </CloseButton>
      <LeftContent>
        <SplashImage src={$image_url('butterflies.jpeg')} alt=""></SplashImage>
        <div
          style={{ marginTop: 'auto', paddingBottom: 'clamp(10px, 5vh, 48px)' }}
        >
          <SocialProofUniversityFooter
            header={'Trusted by academics at 15,770 universities'}
            includeTopBorder={false}
            dark={true}
            narrowGap={true}
          ></SocialProofUniversityFooter>
        </div>
      </LeftContent>
      <MainContent>
        <HeadingContent>
          <HeadingIcon
            src={$image_url('line_illustrations/transparent/atomic_model.svg')}
            alt=""
          />
          {headerText}
        </HeadingContent>
        <TopSpacer></TopSpacer>
        <FeatureList>
          <FeatureItem>
            <CheckIcon name={'check'} size={48} />
            Get highly curated PDF packages
          </FeatureItem>
          <FeatureItem>
            <CheckIcon name={'check'} size={48} />
            Track your impact with Mentions
          </FeatureItem>
          <FeatureItem>
            <CheckIcon name={'check'} size={48} />
            Search faster with advanced filters
          </FeatureItem>
          <FeatureItem>
            <CheckIcon name={'check'} size={48} />
            Support Academia’s mission
          </FeatureItem>
          <FeatureItem>
            <CheckIcon name={'check'} size={48} />
            Create your personal website
          </FeatureItem>
        </FeatureList>
        <BottomSpacer></BottomSpacer>
        <BottomContent>
          <DisclaimerContainer>
            {trialAvailable && (
              <Disclaimer>1 month trial. Cancel anytime.</Disclaimer>
            )}
            <PremiumButton
              fullWidth={true}
              inverse={true}
              size={'sm'}
              onClick={() => {
                recordClickthrough(DISMISSIBLE_NAME);
                window.location.href = routes.checkout_path({
                  feature: 'DEFAULT',
                  trigger: 'full-screen-premium-modal',
                });
              }}
            >
              <ImpressionWatcher source={DISMISSIBLE_NAME}>
                {ctaText}
              </ImpressionWatcher>
            </PremiumButton>
          </DisclaimerContainer>
          <TextButton
            size={'sm'}
            inverse={true}
            onClick={onCloseWithRecordDismissal}
          >
            No thanks
          </TextButton>
        </BottomContent>
      </MainContent>
    </FullScreenModal>
  );
};

export default FullScreenPremiumModal;
